import { useCallback, useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export interface CourtMartialEntry {
  courtMartialDate: string;
  courtMartialDetails: string;
}

export interface CourtMartialFormData {
  isAddingEntry?: boolean;
  hasCourtMartial: boolean | null;
  courtMartials: FormListItem<CourtMartialEntry>[];
  draftCourtMartial: CourtMartialEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const getEmptyCourtMartial = (): CourtMartialEntry => ({
  courtMartialDate: '',
  courtMartialDetails: '',
});

function CourtMartialPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<CourtMartialFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'court-martial-page';

  // Date constraints - within last 7 years up to today
  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 7);

  const handleCourtMartialChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        courtMartials: [],
        draftCourtMartial: getEmptyCourtMartial(),
        hasCourtMartial: value,
        isAddingEntry: false,
      });
    } else {
      updateData({
        ...data,
        hasCourtMartial: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<CourtMartialEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<CourtMartialEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        courtMartials: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: CourtMartialEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.courtMartials;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        courtMartials: itemsToUse,
        draftCourtMartial: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateCourtMartial = useCallback((item: CourtMartialEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.courtMartialDate) {
      errors.courtMartialDate = 'Date of the court-martial is required';
    }
    if (!item.courtMartialDetails) {
      errors.courtMartialDetails = 'Explanation of the court-martial is required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: CourtMartialEntry): boolean => {
    return !draft.courtMartialDate && !draft.courtMartialDetails;
  }, []);

  const renderEditForm = useCallback(
    (item: CourtMartialEntry, onChange: (item: CourtMartialEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = <K extends keyof CourtMartialEntry>(field: K, value: CourtMartialEntry[K]) => {
        onChange({
          ...item,
          [field]: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-date`}>
            <DatePicker
              required
              data-testid={`${baseTestId}-entry-date-picker`}
              errorMessage={errors.courtMartialDate}
              fromDate={minDate}
              id='courtMartialDate'
              label='Date of the court-martial'
              name='courtMartialDate'
              toDate={today}
              tooltipText='Enter the date of the court-martial'
              value={item.courtMartialDate || ''}
              onDateChange={(date, formattedDate) => handleFieldChange('courtMartialDate', formattedDate || '')}
            />
          </div>

          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-details`}>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-entry-details-field`}
              errorMessage={errors.courtMartialDetails}
              label='Explanation of the court-martial'
              name='courtMartialDetails'
              rows={5}
              tooltipText='Provide details including an explanation of the charges, the outcome, and any sentence imposed'
              value={item.courtMartialDetails || ''}
              onChange={(e) => handleFieldChange('courtMartialDetails', e.target.value)}
            />
          </div>
        </div>
      );
    },
    [minDate, today],
  );

  const renderItemSummary = useCallback((item: CourtMartialEntry) => {
    const truncatedDetails =
      item.courtMartialDetails.length > 50
        ? `${item.courtMartialDetails.substring(0, 50)}...`
        : item.courtMartialDetails;

    return {
      details: truncatedDetails,
      title: item.courtMartialDate,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: CourtMartialEntry) => {
    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Date</span>
          <span className='form-list__readonly-value'>{item.courtMartialDate}</span>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Details</span>
          <span className='form-list__readonly-value'>{item.courtMartialDetails}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    courtMartialDate: fieldErrors?.['draftCourtMartial.courtMartialDate'],
    courtMartialDetails: fieldErrors?.['draftCourtMartial.courtMartialDetails'],
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-court-martial`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-court-martial-dropdown`}
          errorMessage={fieldErrors?.hasCourtMartial}
          label='Have you been convicted by a military court-martial in the past 7 years? (If no military service, answer "No.")'
          name='hasCourtMartial'
          options={yesNoOptions}
          tooltipText='This question applies to military personnel who may have been convicted by a military court-martial. If you have never served in the military, answer "No."'
          value={data.hasCourtMartial}
          onChange={handleCourtMartialChange}
        />
      </div>

      {data.hasCourtMartial === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<CourtMartialEntry>
            addLinkText='Add Another Court-Martial'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftCourtMartial}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyCourtMartial}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.courtMartials}
            label='Court-Martial Details'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details about each court-martial conviction within the last 7 years.'
            validateItem={validateCourtMartial}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Court-Martial Convictions'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Military Court-Martial Details</h3>
          <p>
            This section asks if you&apos;ve been convicted in a military court-martial in the last 7 years. This is
            required for the Declaration of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>What is a Military Court-Martial?</h4>
          <p>
            A court-martial is a military court that handles cases involving military members who break military laws.
            There are three types of court-martials:
          </p>
          <ul>
            <li>Summary</li>
            <li>Special</li>
            <li>General</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Who Should Answer &quot;Yes&quot;</h4>
          <p>
            You should answer &quot;Yes&quot; if you have been convicted by any type of military court-martial within
            the last 7 years, regardless of the offense or sentence.
          </p>

          <h4 className='ds-u-margin-top--3'>Who Should Answer &quot;No&quot;</h4>
          <ul>
            <li>Individuals who have never served in the military</li>
            <li>Military personnel who have not been convicted by a court-martial</li>
            <li>Those whose court-martial conviction was more than 7 years ago</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Date of the court-martial</h4>
          <p>Provide the date when the court-martial conviction took place.</p>

          <h4 className='ds-u-margin-top--3'>Explanation of the court-martial</h4>
          <p>
            Enter details about the military court-martial conviction you had within the last 7 years. Describe the
            events that led to it. Below, you can enter details about where it happened and the military authority that
            was involved.
          </p>
          {/* TODO: Restore occurrence and military authority/court guidance when ACP-788 is completed with those fields. */}
          {/*<h4 className='ds-u-margin-top--3'>Place of the occurrence</h4>
          <p>Enter the name of the location where the event occurred.</p>
          <h4 className='ds-u-margin-top--3'>Name of the military authority involved</h4>
          <p>Enter the name of the military authority that was involved in the court-martial.</p>
          <h4 className='ds-u-margin-top--3'>Military Authority or Court Address</h4>
          <p>Enter the address of the military authority that was involved in the military court-martial.</p>
          <ul>
            <li>
              <strong>Address Line 1:</strong> Enter the military authority or court's street address.
            </li>
            <li>
              <strong>Address Line 2:</strong> Enter the military authority or court's suite, floor, or building number
              (if this applies).
            </li>
            <li>
              <strong>City:</strong> Enter the city name of the military authority or court.
            </li>
            <li>
              <strong>State:</strong> Select the military authority or court's state from the dropdown.
            </li>
            <li>
              <strong>Zip Code</strong> Enter the military authority or court's 5-digit ZIP code.
            </li>
          </ul>*/}

          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you have more than one military court-martial to detail, use the &quot;Add Another
            Court-Martial&quot; button to add each one.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default CourtMartialPage;
